// Plan → List view: the MVP schedule board. Loads slots from Notion (kept live
// by the fft-scheduling Worker), shows live guest counts + who marked themselves
// ✨-available per slot, lets ops stage a guide per slot, then publishes the
// assignments to Notion via the 3-tier UPSERT (dry-run first).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Users, Sparkles, Save, Trash2 } from "lucide-react";
import type { GuideRef, PlanSlot } from "@/lib/load";
import { fetchSlots, guideRefById } from "@/lib/load";
import { demoSlots } from "@/lib/demoData";
import { suggestAssignments, suggestionCount, type Suggestion, type SuggestionMap } from "@/lib/suggest";
import { candidatesFor } from "@/lib/eligibility";
import { guideByPeekId, lookupGuideByAnyName } from "@/config/guides";
import PlanBoard from "@/views/PlanBoard";
import { summarizeSlots, isGap } from "@/lib/summary";
import { prepareChanges, pushChanges, peekAssign, type PushResponse, type Staged, type PeekAssignResult } from "@/lib/publish";
import { listDrafts, saveDraft, loadDraft, deleteDraft, type DraftMeta } from "@/lib/drafts";
import { azTodayISO, plusDaysISO } from "@/lib/dates";

const MARKET_STYLES: Record<string, string> = {
  FLG: "bg-emerald-100 text-emerald-700",
  PSCT: "bg-amber-100 text-amber-700",
  TUS: "bg-sky-100 text-sky-700",
};

const isDemo = () =>
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("demo");

export default function PlanList() {
  const [from, setFrom] = useState(azTodayISO());
  const [to, setTo] = useState(plusDaysISO(azTodayISO(), 60));
  const [slots, setSlots] = useState<PlanSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staged, setStaged] = useState<Record<string, GuideRef>>({});
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<PushResponse | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [gapsOnly, setGapsOnly] = useState(false);
  const [peekResults, setPeekResults] = useState<{ slot: PlanSlot; guide: GuideRef; result: PeekAssignResult }[] | null>(null);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");
  const [drafts, setDrafts] = useState<DraftMeta[]>([]);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const autoSaveTimer = useRef<number | null>(null);
  const [scopeVal, setScopeVal] = useState("all"); // "all" | market | "guide:<id>"
  const [dryRun, setDryRun] = useState(false);
  const [draftNotes, setDraftNotes] = useState(""); // operator month-context notes
  const [layout, setLayout] = useState<"list" | "grid" | "guides">("list");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      if (isDemo()) {
        setSlots(demoSlots());
        setNote("Demo data (?demo=1) — not live Notion.");
      } else {
        setSlots(await fetchSlots(from, to));
        setNote(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slotById = useMemo(() => {
    const m = new Map<string, PlanSlot>();
    for (const s of slots) m.set(s.pageId, s);
    return m;
  }, [slots]);

  const summary = useMemo(() => summarizeSlots(slots), [slots]);
  const visibleSlots = useMemo(
    () => (gapsOnly ? slots.filter(isGap) : slots),
    [slots, gapsOnly],
  );

  const grouped = useMemo(() => {
    const g = new Map<string, PlanSlot[]>();
    for (const s of visibleSlots) {
      if (!g.has(s.date)) g.set(s.date, []);
      g.get(s.date)!.push(s);
    }
    return [...g.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [visibleSlots]);

  // Guide view: group slots by their effective guide (staged draft ?? current
  // Notion — Peek id first, then display-name lookup, matching the Grid).
  const guideGroups = useMemo(() => {
    const byName = (s: PlanSlot): GuideRef | null => {
      for (const n of s.assignedGuideNames) {
        const g = lookupGuideByAnyName(n);
        if (g) return guideRefById(g.id);
      }
      return null;
    };
    const map = new Map<string, { guide: GuideRef | null; slots: PlanSlot[] }>();
    for (const s of visibleSlots) {
      const g =
        staged[s.pageId] ??
        (s.assignedGuidePeekId ? guideRefById(s.assignedGuidePeekId) : null) ??
        byName(s);
      const key = g ? g.id : "__none";
      if (!map.has(key)) map.set(key, { guide: g, slots: [] });
      map.get(key)!.slots.push(s);
    }
    const arr = [...map.values()];
    arr.forEach((grp) => grp.slots.sort((x, y) => (x.date + x.hhmm).localeCompare(y.date + y.hhmm)));
    arr.sort((a, b) => {
      if (!a.guide) return 1;
      if (!b.guide) return -1;
      return b.slots.length - a.slots.length || a.guide.peekName.localeCompare(b.guide.peekName);
    });
    return arr;
  }, [visibleSlots, staged]);

  // Staged-aware: a guide the operator just staged (e.g. in the Grid) counts
  // toward load and their slots aren't re-suggested — no double-booking.
  const suggestions: SuggestionMap = useMemo(
    () => suggestAssignments(slots, staged),
    [slots, staged],
  );
  const suggestCount = suggestionCount(suggestions);

  const autoFill = () => {
    setStaged((prev) => {
      const next = { ...prev };
      for (const [pageId, s] of Object.entries(suggestions)) {
        if (s && !next[pageId]) next[pageId] = s.guide;
      }
      return next;
    });
  };

  const stagedList: Staged[] = useMemo(
    () =>
      Object.entries(staged)
        .map(([pageId, guide]) => {
          const slot = slotById.get(pageId);
          return slot ? { slot, guide } : null;
        })
        .filter((s): s is Staged => s != null),
    [staged, slotById],
  );

  // Stable identity so the memoized PlanBoard doesn't re-render per keystroke.
  const stageGuide = useCallback((slot: PlanSlot, guide: GuideRef | null) => {
    setStaged((prev) => {
      const next = { ...prev };
      if (guide) next[slot.pageId] = guide;
      else delete next[slot.pageId];
      return next;
    });
  }, []);

  // ── drafts (persist schedule-building across reload/logout) ──────────────
  const refreshDrafts = useCallback(async () => {
    try {
      setDrafts(await listDrafts());
    } catch {
      /* ignore — drafts unavailable (e.g. no functions server) */
    }
  }, []);

  useEffect(() => {
    if (!isDemo()) void refreshDrafts();
  }, [refreshDrafts]);

  const doSaveDraft = async () => {
    if (isDemo()) {
      setNote("Demo mode — drafts are disabled.");
      return;
    }
    setSavingDraft(true);
    setError(null);
    try {
      const name = draftName.trim() || `Draft ${new Date().toLocaleDateString()}`;
      const r = await saveDraft(name, { staged, from, to, notes: draftNotes }, draftId ?? undefined);
      setDraftId(r.id);
      setDraftName(name);
      setSavedAt(r.updated_at);
      await refreshDrafts();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingDraft(false);
    }
  };

  const loadDraftById = async (id: number) => {
    setError(null);
    try {
      const d = await loadDraft(id);
      const draftFrom = d.payload.from || from;
      const draftTo = d.payload.to || to;
      setStaged(d.payload.staged || {});
      setFrom(draftFrom);
      setTo(draftTo);
      setDraftNotes(d.payload.notes || "");
      setDraftId(d.id);
      setDraftName(d.name);
      setSavedAt(d.updated_at);
      // Re-fetch the DRAFT's window — staged pageIds outside the currently
      // loaded slots would otherwise silently drop from the publish pipeline.
      if (!isDemo()) {
        setLoading(true);
        try {
          setSlots(await fetchSlots(draftFrom, draftTo));
        } finally {
          setLoading(false);
        }
      }
      setNote(`Loaded draft "${d.name}" (${Object.keys(d.payload.staged || {}).length} staged).`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const newDraft = () => {
    setDraftId(null);
    setDraftName("");
    setSavedAt(null);
    setStaged({});
    setDraftNotes("");
  };

  const removeDraft = async (id: number) => {
    try {
      await deleteDraft(id);
      if (id === draftId) newDraft();
      await refreshDrafts();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Auto-save an ACTIVE draft on any edit (debounced) so ops can walk away
  // mid-build and lose nothing.
  useEffect(() => {
    if (draftId == null || isDemo()) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = window.setTimeout(() => {
      saveDraft(draftName || "Untitled draft", { staged, from, to, notes: draftNotes }, draftId)
        .then((r) => setSavedAt(r.updated_at))
        .catch((e) => {
          // Draft was deleted elsewhere (server 404) — detach so the next
          // manual Save creates a fresh draft instead of silently no-oping.
          if (String(e).includes("404")) setDraftId(null);
        });
    }, 1500);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [staged, from, to, draftId, draftName, draftNotes]);

  // ── scoped publish pipeline (all / market / guide) ───────────────────────
  const scopedStaged = useMemo(
    () =>
      stagedList.filter((s) => {
        if (scopeVal === "all") return true;
        if (scopeVal.startsWith("guide:")) return s.guide.id === scopeVal.slice(6);
        return s.slot.market === scopeVal;
      }),
    [stagedList, scopeVal],
  );

  const stagedGuides = useMemo(() => {
    const m = new Map<string, GuideRef>();
    for (const s of stagedList) m.set(s.guide.id, s.guide);
    return [...m.values()].sort((a, b) => a.peekName.localeCompare(b.peekName));
  }, [stagedList]);

  const scopeLabel =
    scopeVal === "all"
      ? "all markets"
      : scopeVal.startsWith("guide:")
        ? stagedGuides.find((g) => g.id === scopeVal.slice(6))?.peekName ?? "guide"
        : scopeVal;

  // Step 1 — push in-scope staged proposals to Notion for guide review. Nothing
  // goes to Peek here (that's a deliberate, post-approval step).
  const pushToNotion = async () => {
    if (isDemo()) {
      setNote("Demo mode — publish is disabled.");
      return;
    }
    const { changes, rejected } = prepareChanges(scopedStaged);
    if (!changes.length) {
      setNote(`Nothing in scope to push${rejected.length ? ` (${rejected.length} unmapped guide[s])` : ""}.`);
      return;
    }
    setPublishing(true);
    setError(null);
    setPeekResults(null);
    try {
      const res = await pushChanges(changes, dryRun);
      setResult(res);
      setNote(
        `${dryRun ? "Dry-run" : "Pushed"} to Notion (${scopeLabel}): ${res.ok} ok, ${res.fail} failed` +
          (rejected.length ? ` · ${rejected.length} unmapped` : "") +
          ". Guides review in Notion → then Publish to Peek.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  };

  // Two-way sync — pull the CURRENT Notion assignments (in scope) into the draft,
  // so edits made during guide review are reflected before the Peek push.
  const pullFromNotion = () => {
    const updates: Record<string, GuideRef> = {};
    for (const slot of slots) {
      if (scopeVal !== "all") {
        if (scopeVal.startsWith("guide:")) {
          if (slot.assignedGuidePeekId !== scopeVal.slice(6)) continue;
        } else if (slot.market !== scopeVal) continue;
      }
      if (!slot.assignedGuidePeekId) continue;
      const g = guideRefById(slot.assignedGuidePeekId);
      if (g) updates[slot.pageId] = g;
    }
    if (Object.keys(updates).length === 0) {
      setNote(`No current Notion assignments to pull (${scopeLabel}).`);
      return;
    }
    setStaged((prev) => ({ ...prev, ...updates }));
    setNote(`Pulled ${Object.keys(updates).length} Notion assignment(s) (${scopeLabel}) into the draft.`);
  };

  // Step 3 — publish the in-scope draft to Peek (guarded add-only), after approval.
  const publishToPeek = async () => {
    if (isDemo()) {
      setNote("Demo mode — publish is disabled.");
      return;
    }
    if (!scopedStaged.length) {
      setNote("Nothing in scope to publish to Peek.");
      return;
    }
    setPublishing(true);
    setError(null);
    setPeekResults(null);
    try {
      const peek = await Promise.all(
        scopedStaged.map(async (s) => ({
          slot: s.slot,
          guide: s.guide,
          result: await peekAssign(s.slot.slotKey, s.guide, dryRun),
        })),
      );
      setPeekResults(peek);
      const added = peek.filter((p) => p.result.decision === "assign" || p.result.decision === "assigned").length;
      const already = peek.filter((p) => p.result.decision === "already-assigned").length;
      const blocked = peek.filter((p) => p.result.decision === "blocked-has-other-guide").length;
      const err = peek.filter((p) => !p.result.ok && p.result.decision !== "blocked-has-other-guide").length;
      setNote(
        `${dryRun ? "Dry-run" : "Published"} to Peek (${scopeLabel}): ${added} ${dryRun ? "would add" : "added"}, ${already} already, ${blocked} need Peek UI` +
          (err ? `, ${err} err` : ""),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  };

  const stagedCount = stagedList.length;

  return (
    <div className="space-y-4">
      {/* Drafts */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Draft</span>
        <input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="Name this draft…"
          className="min-w-[10rem] rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900"
        />
        <button
          onClick={() => void doSaveDraft()}
          disabled={savingDraft}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {savingDraft ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {draftId != null ? "Save" : "Save draft"}
        </button>
        {drafts.length > 0 && (
          <select
            value={draftId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v) void loadDraftById(Number(v));
            }}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900"
          >
            <option value="">Load draft…</option>
            {drafts.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} · {new Date(d.updated_at).toLocaleString()}
              </option>
            ))}
          </select>
        )}
        {draftId != null && (
          <button onClick={newDraft} className="text-xs font-medium text-slate-500 hover:underline">
            New
          </button>
        )}
        {draftId != null && (
          <button
            onClick={() => void removeDraft(draftId)}
            title="Delete this draft"
            className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:underline"
          >
            <Trash2 size={12} /> Delete
          </button>
        )}
        {savedAt && (
          <span className="ml-auto text-xs text-emerald-600">saved {new Date(savedAt).toLocaleTimeString()}</span>
        )}
      </div>

      {/* Operator month-context notes (persisted with the draft) */}
      <textarea
        value={draftNotes}
        onChange={(e) => setDraftNotes(e.target.value)}
        placeholder="Month context / operator notes — e.g. 'want Shane leading more Prescott ghost tours; Dev back on Wednesdays; review-ratio favors newer guides this month'…"
        rows={2}
        className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 placeholder:text-slate-400"
      />

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <label className="flex flex-col text-xs font-medium text-slate-500">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900"
          />
        </label>
        <label className="flex flex-col text-xs font-medium text-slate-500">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900"
          />
        </label>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          {loading ? "Loading…" : "Load slots"}
        </button>
        {suggestCount > 0 && (
          <button
            onClick={autoFill}
            title="Stage the fairest guide for every open slot — ✨ preferred first, unmarked guides as fallback (never ❌)"
            className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
          >
            <Sparkles size={15} />
            Auto-fill {suggestCount}
          </button>
        )}
        <span className="text-sm text-slate-500">
          {slots.length} slot{slots.length === 1 ? "" : "s"}
        </span>
      </div>

      {slots.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Chip label="assigned" n={summary.assigned} cls="bg-slate-100 text-slate-600" />
          <Chip label="fillable ✨" n={summary.fillable} cls="bg-emerald-100 text-emerald-700" />
          {summary.coverable > 0 && (
            <Chip label="no ✨ yet" n={summary.coverable} cls="bg-amber-100 text-amber-700" />
          )}
          <button
            onClick={() => setGapsOnly((v) => !v)}
            disabled={summary.gaps === 0 && !gapsOnly}
            title="Open slots with nobody assignable — every eligible guide marked ❌ (or no eligible guide exists)"
            className={
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium disabled:cursor-default " +
              (summary.gaps > 0 ? "bg-red-100 text-red-700 hover:bg-red-200 " : "bg-slate-100 text-slate-400 ") +
              (gapsOnly ? "ring-2 ring-red-400" : "")
            }
          >
            <AlertTriangle size={12} /> {summary.gaps} gap{summary.gaps === 1 ? "" : "s"}
            {gapsOnly ? " · showing only" : ""}
          </button>
          {summary.excluded > 0 && (
            <Chip label="excluded" n={summary.excluded} cls="bg-slate-100 text-slate-400" />
          )}
        </div>
      )}

      {note && (
        <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">{note}</div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Couldn’t load from Notion.</div>
            <div className="mt-0.5 font-mono text-xs">{error}</div>
            <div className="mt-1 text-red-600">
              Set NOTION_TOKEN + NOTION_DB_ID in <code>.dev.vars</code> and run the Pages
              Functions, or append <code>?demo=1</code> to preview with sample data.
            </div>
          </div>
        </div>
      )}

      {/* View toggle: List / Guides */}
      {slots.length > 0 && (
        <div className="flex w-fit items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setLayout("list")}
            className={"rounded-md px-3 py-1 text-sm font-medium " + (layout === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
          >
            List
          </button>
          <button
            onClick={() => setLayout("grid")}
            title="Visual assignment grid — guides × days, click cells to assign"
            className={"rounded-md px-3 py-1 text-sm font-medium " + (layout === "grid" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
          >
            Grid
          </button>
          <button
            onClick={() => setLayout("guides")}
            className={"rounded-md px-3 py-1 text-sm font-medium " + (layout === "guides" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
          >
            Guides
          </button>
        </div>
      )}

      {/* List layout: slots grouped by date */}
      {layout === "list" &&
        grouped.map(([date, daySlots]) => (
        <div key={date} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
            {formatDay(date)}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2 font-medium">Time</th>
                <th className="px-4 py-2 font-medium">Tour</th>
                <th className="px-4 py-2 font-medium">Guests</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Assigned</th>
                <th className="px-4 py-2 font-medium">Assign guide</th>
              </tr>
            </thead>
            <tbody>
              {daySlots.map((s) => (
                <SlotRow
                  key={s.pageId}
                  slot={s}
                  stagedGuide={staged[s.pageId] ?? null}
                  suggestion={suggestions[s.pageId] ?? null}
                  onStage={stageGuide}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Grid layout: visual assignment board (guides × days, click to assign) */}
      {layout === "grid" && (
        <PlanBoard
          slots={visibleSlots}
          staged={staged}
          suggestions={suggestions}
          onStage={stageGuide}
        />
      )}

      {/* Guides layout: slots grouped by their effective guide (draft ?? Notion) */}
      {layout === "guides" &&
        guideGroups.map((grp) => (
          <div key={grp.guide?.id ?? "none"} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
              <span className="text-sm font-semibold text-slate-700">
                {grp.guide ? grp.guide.peekName : "Unassigned"}
              </span>
              <span className="text-xs text-slate-500">
                {grp.slots.length} tour{grp.slots.length === 1 ? "" : "s"}
                {grp.guide?.region ? ` · ${grp.guide.region}` : ""}
              </span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {grp.slots.map((s) => (
                  <tr key={s.pageId} className="border-t border-slate-100">
                    <td className="whitespace-nowrap px-4 py-1.5 text-slate-500">{formatDay(s.date)}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-medium text-slate-700">{s.startLabel || s.hhmm}</td>
                    <td className="px-2 py-1.5 text-slate-700">
                      <span className={"mr-1.5 rounded px-1 py-0.5 text-[10px] font-semibold " + (s.market ? MARKET_STYLES[s.market] : "bg-slate-100 text-slate-500")}>
                        {s.market ?? "?"}
                      </span>
                      {s.tour}
                    </td>
                    <td className="whitespace-nowrap px-4 py-1.5 text-right text-slate-500">
                      {s.guests != null ? `${s.guests} pax` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {!loading && slots.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          No slots in range. Adjust the dates and Load, or try <code>?demo=1</code>.
        </div>
      )}

      {/* Publish pipeline: draft → Notion (review) → Peek (after approval) */}
      {stagedCount > 0 && (
        <div className="sticky bottom-4 space-y-2 rounded-xl border border-slate-300 bg-white p-4 shadow-lg">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Scope</span>
            <select
              value={scopeVal}
              onChange={(e) => setScopeVal(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900"
            >
              <option value="all">All markets</option>
              <option value="FLG">Flagstaff</option>
              <option value="PSCT">Prescott</option>
              <option value="TUS">Tucson</option>
              {stagedGuides.length > 0 && <option disabled>── guides ──</option>}
              {stagedGuides.map((g) => (
                <option key={g.id} value={`guide:${g.id}`}>
                  {g.peekName}
                </option>
              ))}
            </select>
            <span className="text-sm text-slate-600">
              <span className="font-semibold">{scopedStaged.length}</span> in scope · {stagedCount} staged
            </span>
            <label className="ml-auto flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              dry-run
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => pullFromNotion()}
              disabled={publishing}
              title="Two-way sync: pull current Notion assignments into the draft"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={14} /> Pull from Notion
            </button>
            <button
              onClick={() => void pushToNotion()}
              disabled={publishing || scopedStaged.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {publishing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              Push to Notion (review)
            </button>
            <button
              onClick={() => void publishToPeek()}
              disabled={publishing || scopedStaged.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              <CheckCircle2 size={15} /> Publish to Peek
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            Build draft → Push to Notion for guide review → Pull to reconcile edits → Publish to Peek after approval.
          </p>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <div className="font-medium text-slate-700">
            {result.dryRun ? "Dry-run result" : "Publish result"} — Notion by tier:{" "}
            {Object.entries(result.byTier)
              .map(([t, n]) => `${t}=${n}`)
              .join(", ") || "none"}
          </div>
        </div>
      )}

      {peekResults && peekResults.some((p) => p.result.decision === "blocked-has-other-guide" || !p.result.ok) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          <div className="font-medium text-amber-800">Peek — needs manual handling in the Peek UI:</div>
          <ul className="mt-1 space-y-0.5 text-amber-700">
            {peekResults
              .filter((p) => p.result.decision === "blocked-has-other-guide")
              .map((p) => (
                <li key={"b" + p.slot.pageId}>
                  · {p.slot.date} {p.slot.startLabel} {p.slot.tour} → {p.guide.peekName}: slot already has{" "}
                  {p.result.currentGuides?.map((g) => g.name).join(", ") || "another guide"}
                </li>
              ))}
            {peekResults
              .filter((p) => !p.result.ok && p.result.decision !== "blocked-has-other-guide")
              .map((p) => (
                <li key={"e" + p.slot.pageId}>
                  · {p.slot.date} {p.slot.startLabel} {p.slot.tour} → {p.guide.peekName}: {p.result.reason || "error"}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SlotRow({
  slot,
  stagedGuide,
  suggestion,
  onStage,
}: {
  slot: PlanSlot;
  stagedGuide: GuideRef | null;
  suggestion: Suggestion | null;
  onStage: (slot: PlanSlot, guide: GuideRef | null) => void;
}) {
  const marketCls = slot.market ? MARKET_STYLES[slot.market] : "bg-slate-100 text-slate-500";
  const cap = slot.capacity ?? null;
  const guests = slot.guests ?? null;
  const candidates = candidatesFor(slot);
  const assignable = [...candidates.preferred, ...candidates.open];
  // A draft can stage a guide who has since marked ❌ (or fell off the roster) —
  // keep them visible in the select instead of silently showing blank.
  const stagedOutside =
    stagedGuide && !assignable.some((g) => g.id === stagedGuide.id) ? stagedGuide : null;
  return (
    <tr className={"border-t border-slate-100 " + (stagedGuide ? "bg-emerald-50/60" : "")}>
      <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-700">{slot.startLabel || slot.hhmm}</td>
      <td className="px-4 py-2">
        <span className={"mr-2 rounded px-1.5 py-0.5 text-xs font-semibold " + marketCls}>
          {slot.market ?? "?"}
        </span>
        <span className="text-slate-700">{slot.tour}</span>
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-slate-600">
        <span className="inline-flex items-center gap-1">
          <Users size={13} className="text-slate-400" />
          {guests != null ? guests : "—"}
          {cap != null ? <span className="text-slate-400">/{cap}</span> : null}
        </span>
      </td>
      <td className="px-4 py-2">
        {slot.status.length ? (
          slot.status.map((st) => (
            <span key={st} className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
              {st}
            </span>
          ))
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-slate-600">
        {slot.assignedGuideNames.length ? slot.assignedGuideNames.join(", ") : <span className="text-xs text-slate-400">unassigned</span>}
      </td>
      <td className="px-4 py-2">
        {assignable.length || stagedOutside ? (
          <div className="space-y-1">
            <select
              value={stagedGuide?.id ?? ""}
              onChange={(e) => {
                const pool = stagedOutside ? [...assignable, stagedOutside] : assignable;
                onStage(slot, pool.find((x) => x.id === e.target.value) ?? null);
              }}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900"
            >
              <option value="">— pick a guide —</option>
              {candidates.preferred.length > 0 && (
                <optgroup label="✨ Preferred">
                  {candidates.preferred.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.peekName}
                      {guideByPeekId[g.id]?.inactive ? " (benched)" : ""}
                      {g.pageId ? "" : " (unmapped)"}
                    </option>
                  ))}
                </optgroup>
              )}
              {candidates.open.length > 0 && (
                <optgroup label="No response — assignable">
                  {candidates.open.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.peekName}
                      {g.pageId ? "" : " (unmapped)"}
                    </option>
                  ))}
                </optgroup>
              )}
              {stagedOutside && (
                <optgroup label="Staged (no longer offered)">
                  <option value={stagedOutside.id}>{stagedOutside.peekName}</option>
                </optgroup>
              )}
              {candidates.unavailable.some((g) => g.id !== stagedOutside?.id) && (
                <optgroup label="❌ Unavailable">
                  {candidates.unavailable
                    .filter((g) => g.id !== stagedOutside?.id)
                    .map((g) => (
                      <option key={g.id} value={g.id} disabled>
                        {g.peekName}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
            {suggestion && stagedGuide?.id !== suggestion.guide.id && (
              <button
                onClick={() => onStage(slot, suggestion.guide)}
                title="Fairest assignable guide for this slot"
                className="flex items-center gap-1 text-xs text-amber-700 hover:underline"
              >
                <Sparkles size={11} /> suggest: {suggestion.guide.peekName}
                {suggestion.tier === "open" ? " (no response)" : ""}
              </button>
            )}
          </div>
        ) : (
          <span className="text-xs text-slate-400">
            {slot.excluded ? "excluded" : "nobody assignable (all ❌)"}
          </span>
        )}
      </td>
    </tr>
  );
}

function formatDay(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function Chip({ label, n, cls }: { label: string; n: number; cls: string }) {
  return (
    <span className={"inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium " + cls}>
      <span className="font-semibold">{n}</span> {label}
    </span>
  );
}
